/**
 * RosterTeamSearch
 *
 * Debounced team typeahead for the roster-image view. Mirrors the proven
 * debounce + click-outside + escape pattern used elsewhere (TeamTypeahead /
 * PlayersSection) but is tailored to roster selection: it surfaces a
 * `hasRosterImage` indicator and presents every match as a disambiguation list
 * rather than auto-opening a single result [SpecFlow E6].
 *
 * View-only: selecting a team hands its id/name to the parent, which resolves
 * and displays the roster image. No authoring here.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { searchTeams } from '../../../../services/teams';
import { SEARCH_DEBOUNCE_MS } from '../../../../constants/extraction';
import type { TeamSearchResult } from '../../../../types/api';
import './RosterTeamSearch.css';

interface RosterTeamSearchProps {
  /** Name of the currently-selected team, shown in the input. */
  selectedTeamName: string | null;
  onSelectTeam: (team: TeamSearchResult) => void;
  onClear: () => void;
}

export function RosterTeamSearch({
  selectedTeamName,
  onSelectTeam,
  onClear,
}: RosterTeamSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<TeamSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced search. Empty/whitespace term or a closed dropdown clears results
  // without firing a request.
  useEffect(() => {
    if (!searchTerm.trim() || !isOpen) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const searchResults = await searchTeams(searchTerm.trim());
        if (!cancelled) setResults(searchResults);
      } catch (err) {
        console.error('[RosterTeamSearch] Failed to search teams', err);
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm, isOpen]);

  // Click outside / escape to close.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = useCallback(
    (team: TeamSearchResult) => {
      onSelectTeam(team);
      setSearchTerm('');
      setResults([]);
      setIsOpen(false);
    },
    [onSelectTeam],
  );

  const handleClear = () => {
    onClear();
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const trimmed = searchTerm.trim();
  const hasSelection = Boolean(selectedTeamName);

  return (
    <div className="roster-team-search" ref={wrapperRef}>
      <div className="roster-team-search__input-wrapper">
        {hasSelection ? (
          <div className="roster-team-search__selected">
            <span className="roster-team-search__selected-name">
              {selectedTeamName}
            </span>
            <button
              type="button"
              className="roster-team-search__clear"
              onClick={handleClear}
              aria-label="Clear team selection"
              data-testid="roster-team-search-clear"
            >
              ×
            </button>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            className="roster-team-search__input"
            placeholder="Search teams by name"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            autoComplete="off"
            aria-label="Search teams by name"
            data-testid="roster-team-search-input"
          />
        )}
      </div>

      {isOpen && !hasSelection && (
        <div
          className="roster-team-search__dropdown"
          role="listbox"
          aria-label="Team search results"
        >
          {isLoading ? (
            <div className="roster-team-search__loading">
              <div className="roster-team-search__spinner" />
              <span>Searching…</span>
            </div>
          ) : !trimmed ? (
            <div className="roster-team-search__empty">
              Start typing to search teams
            </div>
          ) : results.length === 0 ? (
            <div className="roster-team-search__empty">
              No teams match “{trimmed}”
            </div>
          ) : (
            results.map((team) => (
              <button
                key={team.id}
                type="button"
                role="option"
                aria-selected={false}
                className="roster-team-search__option"
                onClick={() => handleSelect(team)}
                data-testid={`roster-team-search-option-${team.id}`}
              >
                <span className="roster-team-search__option-name">
                  {team.name}
                </span>
                {team.hasRosterImage ? (
                  <span className="roster-team-search__badge">Has roster</span>
                ) : (
                  <span className="roster-team-search__badge roster-team-search__badge--muted">
                    No roster
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
